import { SiteData, PublishedSiteSnapshot, BlogPost, YachtItem } from '../types';

export class SeoService {
  public static generateSitemapXml(
    siteOrSnapshot: SiteData | PublishedSiteSnapshot,
    customDomain?: string,
    extraData?: { blogPosts?: BlogPost[]; yachts?: YachtItem[] }
  ): string {
    const isSiteData = 'ownerId' in siteOrSnapshot;
    const customDom = customDomain || (isSiteData ? siteOrSnapshot.domains?.find((d) => d.isPrimary)?.domain : undefined);
    const baseUrl = customDom
      ? `https://${customDom}`
      : `https://www.adiosoft.com/${siteOrSnapshot.subdomain || 'site'}`;

    const pages = isSiteData
      ? siteOrSnapshot.publishedPages || siteOrSnapshot.pages || []
      : siteOrSnapshot.pages || [];
    const blogs = extraData?.blogPosts || ('blogPosts' in siteOrSnapshot ? siteOrSnapshot.blogPosts : []) || [];
    const yachts = extraData?.yachts || ('yachts' in siteOrSnapshot ? siteOrSnapshot.yachts : []) || [];
    const updatedAtTimestamp = isSiteData ? siteOrSnapshot.updatedAt : siteOrSnapshot.publishedAt;
    const updatedAtDate = new Date(updatedAtTimestamp || Date.now()).toISOString().split('T')[0];

    const urls: string[] = [];

    // 1. Static Published Pages
    pages
      .filter((p) => p.isPublished !== false)
      .forEach((p) => {
        const cleanSlug = p.slug === '/' ? '' : p.slug.startsWith('/') ? p.slug : `/${p.slug}`;
        urls.push(`  <url>
    <loc>${baseUrl}${cleanSlug}</loc>
    <lastmod>${updatedAtDate}</lastmod>
    <changefreq>${p.isHomePage ? 'daily' : 'weekly'}</changefreq>
    <priority>${p.isHomePage ? '1.0' : '0.8'}</priority>
  </url>`);
      });

    // 2. Published Blog Posts
    blogs
      .filter((b) => b.status === 'PUBLISHED')
      .forEach((b) => {
        const slug = b.slug || b.id;
        urls.push(`  <url>
    <loc>${baseUrl}/blog/${slug}</loc>
    <lastmod>${new Date(b.updatedAt || b.createdAt || Date.now()).toISOString().split('T')[0]}</lastmod>
    <changefreq>weekly</changefreq>
    <priority>0.7</priority>
  </url>`);
      });

    // 3. Published Yacht Fleet Detail Pages
    yachts.forEach((y) => {
      const slug = y.slug || y.id;
      urls.push(`  <url>
    <loc>${baseUrl}/yacht/${slug}</loc>
    <lastmod>${updatedAtDate}</lastmod>
    <changefreq>weekly</changefreq>
    <priority>0.8</priority>
  </url>`);
    });

    return `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
${urls.join('\n')}
</urlset>`;
  }

  public static generateRobotsTxt(siteOrSnapshot: SiteData | PublishedSiteSnapshot, customDomain?: string): string {
    const isSiteData = 'ownerId' in siteOrSnapshot;
    const customDom = customDomain || (isSiteData ? siteOrSnapshot.domains?.find((d) => d.isPrimary)?.domain : undefined);
    const baseUrl = customDom
      ? `https://${customDom}`
      : `https://www.adiosoft.com/${siteOrSnapshot.subdomain || 'site'}`;

    return `User-agent: *
Allow: /
Disallow: /admin/
Disallow: /api/
Disallow: /draft/

Sitemap: ${baseUrl}/sitemap.xml`;
  }

  public static generateHeadMetadata(
    snapshot: PublishedSiteSnapshot,
    currentPath = '/',
    canonicalDomain?: string
  ): string {
    const seo = snapshot?.seo || {};
    const settings = snapshot?.settings || {};
    const domain = canonicalDomain || (snapshot.subdomain ? `www.adiosoft.com/${snapshot.subdomain}` : 'www.adiosoft.com');
    const canonicalUrl = domain.includes('/') || domain.includes('.com')
      ? `https://${domain}${currentPath === '/' ? '' : currentPath}`
      : `https://${domain}${currentPath === '/' ? '' : currentPath}`;

    const title = seo.title || settings.metaTitle || settings.siteName || 'Adiosoft Website';
    const description = seo.description || settings.metaDescription || 'Profesyonel kurumsal web sitesi.';
    const ogImage = seo.ogImage || settings.logoUrl || 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?auto=format&fit=crop&w=1200&q=80';

    return `
<title>${title}</title>
<meta name="description" content="${description}" />
<link rel="canonical" href="${canonicalUrl}" />
<meta property="og:type" content="website" />
<meta property="og:title" content="${title}" />
<meta property="og:description" content="${description}" />
<meta property="og:url" content="${canonicalUrl}" />
<meta property="og:image" content="${ogImage}" />
<meta name="twitter:card" content="summary_large_image" />
<meta name="twitter:title" content="${title}" />
<meta name="twitter:description" content="${description}" />
<meta name="twitter:image" content="${ogImage}" />
    `.trim();
  }
}
